import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import { getPapers, getSubjects, getYears, getSessions, getPdfUrl } from '../api';
import './SubjectDetails.css';
import CustomDropdown from './CustomDropdown';

const SubjectDetails = () => {
  const [curriculum, setCurriculum] = useState('');
  const [subject, setSubject] = useState('');
  const [year, setYear] = useState('');
  const [session, setSession] = useState('');
  const [papers, setPapers] = useState([]);
  const [activeTab, setActiveTab] = useState('papers');
  
  // Initialize as empty arrays so we can safely render even if no data is loaded yet.
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  
  // Adding new loading state flags below existing useState declarations
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const standardCurriculums = ['A-Levels', 'IGCSE', 'O-Levels'];
  const isStandard = standardCurriculums.includes(curriculum);

  const handleViewPdf = (e, paper, viewMode) => {
    const filename = paper[viewMode];
    if (e.metaKey || e.ctrlKey) {
      navigate('/viewer', { state: { filename, viewMode } });
    } else {
      const url = `/viewer?filename=${encodeURIComponent(filename)}&viewMode=${viewMode}`;
      window.open(url, '_blank');
    }
  };

  const handleDownloadZip = (e, paper) => {
    e.preventDefault();
    const url = getPdfUrl(paper.zip);
    window.location.href = url;
  };

  const sortedPapers = React.useMemo(() => {
    if (['A-Levels', 'IGCSE', 'O-Levels'].includes(curriculum)) {
      const expected = [];
      const additional = [];
      const regex = /^Paper\s+(\d+)$/i;
      papers.forEach(p => {
        const match = regex.exec(p.name);
        if (match && match[1]) {
          expected.push({ paper: p, number: parseInt(match[1], 10) });
        } else {
          additional.push(p);
        }
      });
      expected.sort((a, b) => a.number - b.number);
      return { expected: expected.map(item => item.paper), additional };
    } else {
      return { expected: papers, additional: [] };
    }
  }, [papers, curriculum]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCurriculum = params.get('curriculum');
    const urlSubject = params.get('subject');
    const urlYear = params.get('year');
    const urlSession = params.get('session');
    if (urlCurriculum) {
      setCurriculum(urlCurriculum);
      if (['A-Levels', 'IGCSE', 'O-Levels'].includes(urlCurriculum)) {
        (async () => {
          const subjects = await getSubjects(urlCurriculum);
          setSubjectOptions(subjects);
          if (urlSubject) {
            setSubject(urlSubject);
            const yearsData = await getYears(urlCurriculum, urlSubject);
            yearsData.sort((a, b) => Number(b) - Number(a));
            setYearOptions(yearsData);
            if (urlYear) {
              setYear(urlYear);
              const sessionsData = await getSessions(urlCurriculum, urlSubject, urlYear);
              setSessionOptions(sessionsData);
              if (urlSession) {
                setSession(urlSession);
                const result = await getPapers(urlCurriculum, urlSubject, urlYear, urlSession);
                setPapers(result);
              }
            }
          } else {
            // Non-standard branch
            (async () => {
              if (urlYear) {
                setYear(urlYear);
                const result = await getPapers(urlCurriculum, "", urlYear, "");
                setPapers(result);
              } else {
                setLoadingYears(true);
                const yearsData = await getYears(urlCurriculum, "");
                yearsData.sort((a, b) => Number(b) - Number(a));
                setYearOptions(yearsData);
                setLoadingYears(false);
              }
            })();
          }
        })();
      } else {
        // Non-standard branch
        (async () => {
          if (urlYear) {
            setYear(urlYear);
            const result = await getPapers(urlCurriculum, "", urlYear, "");
            setPapers(result);
          } else {
            setLoadingYears(true);
            const yearsData = await getYears(urlCurriculum, "");
            yearsData.sort((a, b) => Number(b) - Number(a));
            setYearOptions(yearsData);
            setLoadingYears(false);
          }
        })();
      }
    }
  }, [location.search]);

  const handleCurriculumChange = async (selectedCurriculum) => {
    setCurriculum(selectedCurriculum);
    if (['A-Levels', 'IGCSE', 'O-Levels'].includes(selectedCurriculum)) {
      setSubject('');
      setSubjectOptions([]);
      setYear('');
      setYearOptions([]);
      setSession('');
      setSessionOptions([]);
      if (selectedCurriculum) {
        setLoadingSubjects(true);
        const subjects = await getSubjects(selectedCurriculum);
        setSubjectOptions(subjects);
        setLoadingSubjects(false);
      }
    } else {
      // Non-standard: no subject/session. Fetch years directly.
      setYear('');
      setYearOptions([]);
      if (selectedCurriculum) {
        setLoadingYears(true);
        const yearsData = await getYears(selectedCurriculum, "");
        yearsData.sort((a, b) => Number(b) - Number(a));
        setYearOptions(yearsData);
        setLoadingYears(false);
      }
    }
  };

  const handleSubjectChange = async (selectedSubject) => {
    setSubject(selectedSubject);
    setYear('');
    setYearOptions([]);
    setSession('');
    setSessionOptions([]);
    if(selectedSubject) {
      setLoadingYears(true);
      const years = await getYears(curriculum, selectedSubject);
      years.sort((a, b) => Number(b) - Number(a));
      setYearOptions(years);
      setLoadingYears(false);
    }
  };

  const handleYearChange = async (selectedYear) => {
    setYear(selectedYear);
    if (['A-Levels', 'IGCSE', 'O-Levels'].includes(curriculum)) {
      setSession('');
      setSessionOptions([]);
      if (subject && selectedYear) {
        setLoadingSessions(true);
        const sessions = await getSessions(curriculum, subject, selectedYear);
        setSessionOptions(sessions);
        setLoadingSessions(false);
      }
    }
  };

  const handleSessionChange = (selectedSession) => {
    setSession(selectedSession);
  };

  // Auto search whenever form fields are complete or changed
  useEffect(() => {
    const isStandard = ['A-Levels', 'IGCSE', 'O-Levels'].includes(curriculum);
    if ((isStandard && curriculum && subject && year && session) ||
        (!isStandard && curriculum && year)) {
      const params = new URLSearchParams();
      params.set('curriculum', curriculum);
      if (isStandard) {
        params.set('subject', subject);
        params.set('year', year);
        params.set('session', session);
      } else {
        params.set('year', year);
      }
      navigate(`?${params.toString()}`, { replace: true });
      (async () => {
        const result = await getPapers(curriculum, isStandard ? subject : "", year, isStandard ? session : "");
        setPapers(result);
      })();
    }
  }, [curriculum, subject, year, session]);

  return (
    <>
      <Header />
      <div className="subject-details-container">
        <h2 style={{ textAlign: 'center', marginTop: '20px' }}>Past Papers & Resources</h2>
        <div className="search-form-container">
          <form>
            <label>
              Curriculum:
              <CustomDropdown
                options={['IGCSE', 'A-Levels', 'O-Levels', 'CCC Sr.']}
                value={curriculum}
                onChange={handleCurriculumChange}
                placeholder="Select Curriculum"
              />
            </label>
            
            {curriculum && isStandard && (
              loadingSubjects ? (
                <div className="loading-spinner">
                  <div className="spinner"></div> Loading subjects...
                </div>
              ) : (
                (subjectOptions.length > 0 || subjectOptions.length === 0) && (
                  <label>
                    Subject:
                    <CustomDropdown 
                      options={subjectOptions} 
                      value={subject} 
                      onChange={handleSubjectChange} 
                      placeholder="Select Subject" 
                    />
                  </label>
                )
              )
            )}

            {(isStandard ? subject : curriculum) && (loadingYears ? (
              <div className="loading-spinner">
                <div className="spinner"></div> Loading years...
              </div>
            ) : (
              <label>
                Year:
                <CustomDropdown 
                  options={yearOptions} 
                  value={year} 
                  onChange={handleYearChange} 
                  placeholder="Select Year" 
                  preserveOrder={true}
                />
              </label>
            ))}

            {year && isStandard && (loadingSessions ? (
              <div className="loading-spinner">
                <div className="spinner"></div> Loading sessions...
              </div>
            ) : (
              <label>
                Session:
                <CustomDropdown 
                  options={sessionOptions} 
                  value={session} 
                  onChange={handleSessionChange} 
                  placeholder="Select Session" 
                />
              </label>
            ))}
          </form>
        </div>
        {papers.length === 0 && (
          <div className="no-results" style={{ textAlign: 'center', marginTop: '20px', fontSize: '1.1em' }}>
            Please enter search criteria above and click 'Search' to view past papers and resources.
          </div>
        )}
        {papers.length > 0 && ['A-Levels', 'IGCSE', 'O-Levels'].includes(curriculum) && (
          <div className="tabs-container">
            <button
              className={activeTab === 'papers' ? 'active' : ''}
              onClick={() => setActiveTab('papers')}
            >
              Past Papers
            </button>
            <button
              className={activeTab === 'resources' ? 'active' : ''}
              onClick={() => setActiveTab('resources')}
            >
              Resources
            </button>
          </div>
        )}
        {activeTab === 'papers' && papers.length > 0 && (
          <div className="papers-list">
            {['A-Levels', 'IGCSE', 'O-Levels'].includes(curriculum) ? (
              <>
                {sortedPapers.expected.length > 0 && sortedPapers.expected.map((paper) => {
                  const match = paper.name.match(/Paper\s+(\d+)/i);
                  const number = match ? match[1] : '';
                  return (
                    <div key={paper.code} className="paper-row">
                      <button 
                        onClick={(e) => handleViewPdf(e, paper, 'qp')} 
                        disabled={!paper.qp} 
                        className={`paper-button qp`}
                      >
                        {paper.name}
                      </button>
                      {paper.ms && (
                        <button 
                          onClick={(e) => handleViewPdf(e, paper, 'ms')}
                          className={`paper-button ms`}
                        >
                          Mark Scheme {number}
                        </button>
                      )}
                      {paper.in && (
                        <button 
                          onClick={(e) => handleViewPdf(e, paper, 'in')}
                          className={`paper-button in`}
                        >
                          Reading Insert {number}
                        </button>
                      )}
                      {paper.zip && (
                        <button 
                          onClick={(e) => handleDownloadZip(e, paper)}
                          className={`paper-button zip`}
                        >
                          Download ZIP
                        </button>
                      )}
                    </div>
                  );
                })}
                {sortedPapers.additional.length > 0 && (
                  <div className="additional-papers" style={{ marginTop: '20px' }}>
                    <h4>Additional Papers</h4>
                    {sortedPapers.additional.map((paper) => (
                      <div key={paper.code} className="paper-row">
                        <button 
                          onClick={(e) => handleViewPdf(e, paper, 'qp')} 
                          disabled={!paper.qp} 
                          className={`paper-button qp`}
                        >
                          {paper.name}
                        </button>
                        {paper.zip && (
                          <button 
                            onClick={(e) => handleDownloadZip(e, paper)}
                            className={`paper-button zip`}
                          >
                            Download ZIP
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              papers.map((paper) => {
                if(paper.zip) {
                  return (
                    <div key={paper.code} className="paper-row">
                      <button 
                        onClick={(e) => handleDownloadZip(e, paper)}
                        className="paper-button zip"
                      >
                        {paper.name}
                      </button>
                    </div>
                  );
                } else {
                  const fileName = paper.qp ? paper.qp.split('/').pop() : paper.name;
                  return (
                    <div key={paper.code} className="paper-row">
                      <button 
                        onClick={(e) => handleViewPdf(e, paper, 'qp')}
                        disabled={!paper.qp}
                        className="paper-button qp"
                      >
                        {fileName}
                      </button>
                    </div>
                  );
                }
              })
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SubjectDetails;